import SubHeaders from '@/components/Headers/SubHeaders'
import TextEditorCustomize, {
    TemplateVariable,
} from '@/components/TextEditorCustomize'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'
import { paths } from '@/utils/path'
import { useEffect, useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import { Button, FormItem, Typogaphy } from 'vnetwork-ui'
import {
    createMailerTemplate,
    getMailerTemplateByID,
    updateMailerTemplate,
} from '../../../services'
import {
    CreateMailerTemplatePayload,
    MailerTemplateVariableItem,
    UpdateMailerTemplatePayload,
} from '../../../types'
import {
    HolidayEmailPreset,
    HOLIDAY_TEMPLATE_TYPE,
} from './holiday-email-presets'
import HolidayPresetButtons from './holiday-preset-buttons'
import { TEMPLATE_TYPE_OPTIONS } from '../../../utils/mailer-template-types'

interface FormValues {
    mailer_template_subject: string
    mailer_template_content: string
    mailer_template_type: string
    mailer_template_status: string
}

// Available colors for variables (auto-assigned by index)
const VARIABLE_COLORS = [
    'blue',
    'green',
    'purple',
    'orange',
    'pink',
    'teal',
    'indigo',
    'cyan',
    'red',
    'yellow',
] as const

// Default variables for new templates
const DEFAULT_TEMPLATE_VARIABLES: MailerTemplateVariableItem[] = [
    {
        name: 'ten_nguoi_nhan',
        label: 'Tên người nhận',
        description: 'Tên đầy đủ của người nhận email',
    },
    { name: 'email', label: 'Email', description: 'Địa chỉ email người nhận' },
    {
        name: 'cong_ty',
        label: 'Công ty',
        description: 'Tên công ty của người nhận',
    },
    {
        name: 'so_dien_thoai',
        label: 'Số điện thoại',
        description: 'Số điện thoại người nhận',
    },
    {
        name: 'chuc_vu',
        label: 'Chức vụ',
        description: 'Chức vụ của người nhận',
    },
    {
        name: 'ten_dich_vu',
        label: 'Tên dịch vụ',
        description: 'Tên dịch vụ đang giới thiệu',
    },
    {
        name: 'nguoi_gui',
        label: 'Người gửi',
        description: 'Tên của bạn (người gửi)',
    },
    {
        name: 'sdt_nguoi_gui',
        label: 'SĐT người gửi',
        description: 'Số điện thoại của bạn',
    },
    {
        name: 'chuc_vu_nguoi_gui',
        label: 'Chức vụ người gửi',
        description: 'Chức vụ của bạn',
    },
    { name: 'ngay', label: 'Ngày', description: 'Ngày gửi email' },
]

// Transform template variables to TemplateVariable format for TextEditor
const transformVariables = (
    variables: MailerTemplateVariableItem[]
): TemplateVariable[] => {
    return variables.map((v, index) => ({
        name: v.name,
        label: v.label,
        description: v.description,
        color: VARIABLE_COLORS[index % VARIABLE_COLORS.length], // Auto-assign color by index
    }))
}

const TemplateDetail: React.FC = () => {
    const { t } = useTranslation()
    const { id } = useParams<{ id: string }>()

    const isNew = !id
    const templateId = isNew ? null : Number(id)

    // Quick Help options for Template Detail page
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'template-subject',
                label: 'Nhập tiêu đề email',
                description: 'Subject hiển thị khi nhận email',
                selector: '[name="mailer_template_subject"]',
                tourTitle: 'Tiêu Đề Email (Subject)',
                tourDescription:
                    'Nhập tiêu đề email. Đây là dòng chữ người nhận thấy đầu tiên trong inbox. Có thể sử dụng biến như {{ten_nguoi_nhan}}.',
            },
            {
                id: 'template-content',
                label: 'Soạn nội dung email',
                description: 'Sử dụng editor với variables',
                selector: '.text-editor-container',
                tourTitle: 'Nội Dung Email',
                tourDescription:
                    'Soạn nội dung email trong editor. Nhấn "Insert Variable" hoặc gõ {{ để thêm biến. Biến sẽ được thay thế bằng dữ liệu thực khi gửi.',
            },
            {
                id: 'save-template',
                label: 'Lưu template',
                description: 'Lưu lại mẫu email',
                buttonLabel: t('save'),
                tourTitle: 'Lưu Template',
                tourDescription:
                    'Nhấn nút này để lưu template. Template đã lưu có thể sử dụng trong nhiều campaign khác nhau.',
            },
        ],
        [t]
    )

    useQuickHelp(quickHelpOptions)

    // Fetch template data
    const { data: template, renderState: renderStateTemplate } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.MAILER_TEMPLATES_DETAIL, templateId],
            queryFn: () => getMailerTemplateByID(templateId!),
            enabled: !!templateId,
        })

    // Use template's variables if available, otherwise use defaults
    const templateVariables = transformVariables(
        template?.mailer_template_variables?.length
            ? template.mailer_template_variables
            : DEFAULT_TEMPLATE_VARIABLES
    )

    // Track current variables (including custom ones added by user)
    const [currentVariables, setCurrentVariables] = useState<
        TemplateVariable[]
    >([])

    // Handle variables change from TextEditor (includes predefined + custom)
    const handleVariablesChange = (variables: TemplateVariable[]) => {
        setCurrentVariables(variables)
    }

    // Convert TemplateVariable[] to MailerTemplateVariableItem[] for saving
    const variablesToSave: MailerTemplateVariableItem[] =
        currentVariables.length > 0
            ? currentVariables.map((v) => ({
                  name: v.name,
                  label: v.label,
                  description: v.description,
              }))
            : DEFAULT_TEMPLATE_VARIABLES
    // Form
    const { control, handleSubmit, reset, watch } = useForm<FormValues>({
        defaultValues: {
            mailer_template_subject: '',
            mailer_template_content: template?.mailer_template_content || '',
            mailer_template_type: template?.mailer_template_type || 'marketing',
            mailer_template_status: '',
        },
    })

    // Watch type to show context-aware caption under the select
    const watchedType = watch('mailer_template_type')

    // Reset form when template data is loaded
    useEffect(() => {
        if (template) {
            reset({
                mailer_template_subject: template.mailer_template_subject,
                mailer_template_content: template.mailer_template_content,
                mailer_template_type:
                    template.mailer_template_type || 'marketing',
            })
        }
    }, [template, reset])

    // Create mutation — toast uses module namespace
    const { mutateAsync: createTemplate, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: (payload: CreateMailerTemplatePayload) =>
                    createMailerTemplate(payload),
            },
            {
                reactQueryKeys: QUERY_KEYS.MAILER_TEMPLATES,
                onSuccess: () =>
                    toast.success(
                        t('mailerCampaign:template_created_successfully')
                    ),
                onError: () =>
                    toast.error(t('mailerCampaign:template_create_failed')),
            }
        )

    // Update mutation — toast uses module namespace
    const { mutateAsync: updateTemplate, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (data: {
                    id: number
                    payload: UpdateMailerTemplatePayload
                }) => updateMailerTemplate(data.id, data.payload),
            },
            {
                reactQueryKeys: QUERY_KEYS.MAILER_TEMPLATES,
                onSuccess: () =>
                    toast.success(
                        t('mailerCampaign:template_updated_successfully')
                    ),
                onError: () =>
                    toast.error(t('mailerCampaign:template_update_failed')),
            }
        )

    // Apply a holiday preset: fills form + registers preset variables as chips
    const handleApplyPreset = (preset: HolidayEmailPreset) => {
        reset({
            mailer_template_subject: preset.subject,
            mailer_template_content: preset.content,
            mailer_template_type: HOLIDAY_TEMPLATE_TYPE,
            mailer_template_status: '',
        })
        setCurrentVariables(transformVariables(preset.variables))
    }

    // Submit handler — toasts handled by mutation hook, errors swallowed there
    const onSubmit = async (data: FormValues) => {
        if (isNew) {
            const payload: CreateMailerTemplatePayload = {
                mailer_template_subject: data.mailer_template_subject,
                mailer_template_content: data.mailer_template_content,
                mailer_template_type: data.mailer_template_type,
                mailer_template_variables: variablesToSave,
            }
            await createTemplate(payload)
        } else if (templateId) {
            const payload: UpdateMailerTemplatePayload = {
                mailer_template_subject: data.mailer_template_subject,
                mailer_template_content: data.mailer_template_content,
                mailer_template_type: data.mailer_template_type,
                mailer_template_variables: variablesToSave,
            }
            await updateTemplate({ id: templateId, payload })
        }
    }

    const stateComponent = renderStateTemplate()
    if (stateComponent) {
        return stateComponent
    }

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.mailerCampaign + '?tab=templates',
                        title: t('mailerCampaign:templates'),
                    },
                    {
                        path: '#',
                        title: isNew
                            ? t('mailerCampaign:create_template')
                            : t('mailerCampaign:templates') +
                                  ' - ' +
                                  template?.mailer_template_subject || '',
                    },
                ]}
            />

            {isNew && <HolidayPresetButtons onApply={handleApplyPreset} />}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
                {/* Basic Information */}
                <div className="bg-white rounded-md p-4 shadow-sm border space-y-4">
                    <Typogaphy className="text-lg font-semibold text-gray-800 mb-2">
                        {t('mailerCampaign:template_information')}
                    </Typogaphy>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0">
                        <FormItem
                            name="mailer_template_subject"
                            type="textfield"
                            control={control}
                            label={t('mailerCampaign:template_subject')}
                            placeholder={t(
                                'mailerCampaign:enter_template_subject'
                            )}
                        />
                        <FormItem
                            name="mailer_template_type"
                            type="select"
                            control={control}
                            label={t('mailerCampaign:template_type')}
                            placeholder={t(
                                'mailerCampaign:enter_template_type'
                            )}
                            options={TEMPLATE_TYPE_OPTIONS}
                            allowClear={false}
                        />
                        <div />
                        <p className="text-xs text-gray-500 mt-2">
                            {watchedType === 'marketing'
                                ? 'Style sales email: kèm chữ ký nhân viên (tên, position, SĐT). Dùng cho lead alert / promo.'
                                : 'Style thông báo KH: layout tự chèn logo, "Customer: ID", greeting "Kính gửi Quý Khách hàng (<tên>)", footer VNETWORK JSC. Subject tự thêm "VNETWORK - ".'}
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Controller
                            name="mailer_template_content"
                            control={control}
                            rules={{
                                required: t(
                                    'mailerCampaign:template_content_required'
                                ),
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <div>
                                    <TextEditorCustomize
                                        value={{
                                            text: field.value,
                                            attachments: [],
                                        }}
                                        onChange={(val) =>
                                            field.onChange(val.text)
                                        }
                                        placeholder={t(
                                            'mailerCampaign:enter_template_content'
                                        )}
                                        disabledAttachments
                                        enableVariables={true}
                                        templateVariables={templateVariables}
                                        allowCustomVariables
                                        onVariablesChange={
                                            handleVariablesChange
                                        }
                                    />
                                    {error && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {error.message}
                                        </p>
                                    )}
                                </div>
                            )}
                        />
                    </div>
                    <div className="flex items-center justify-end">
                        <Button
                            variant="contained"
                            type="submit"
                            className="min-w-[120px]"
                            loading={isUpdating || isCreating}
                            disabled={isUpdating || isCreating}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default TemplateDetail
